import { useRef, useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useLocation } from "wouter"
import { CreateSessionTemplateSchema, type CreateSessionTemplate } from "shared"
import {
  createSessionTemplate,
  fetchAdminPrograms,
  fetchServiceTypes,
} from "../api"
import { AdminButton } from "@rawa/ui/AdminButton"
import { DiscardChangesDialog } from "../components/DiscardChangesDialog"
import {
  EmptyCanvas,
  SessionTemplateBuilder,
} from "../components/SessionTemplateBuilder"
import { SessionTemplateNameFields } from "../components/SessionTemplateNameFields"
import { TEMPLATE_NAMES, useNameTaken } from "../hooks/useNameTaken"
import { MultiSelectField } from "@rawa/ui/MultiSelectField"
import { pathHelpers, paths } from "../../../paths"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { isExpectedApiError } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"

/**
 * Setting up a session template. The design has no separate set-up screen, so
 * a new template is named in the builder itself, in the details panel that
 * shows a saved one's name; saving it opens the template on its own screen,
 * where modules are placed.
 */
export function NewSessionTemplatePage() {
  const [, navigate] = useLocation()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [programIds, setProgramIds] = useState<string[]>([])
  const [serviceTypeIds, setServiceTypeIds] = useState<string[]>([])
  const [leaving, setLeaving] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Only programmes ICIS still has active are offered. A list that did not
  // load is not a reason to hold the form up, since a template can be set up
  // for no programme yet; the cache records the fault.
  const { data: programs = [] } = useQuery({
    queryKey: ["programs"],
    queryFn: ({ signal }) => fetchAdminPrograms(signal),
    select: (all) => all.filter((program) => program.active),
    retry: false,
    meta: { source: "fetchAdminPrograms" },
  })
  // Already filtered to active by the endpoint, unlike programmes.
  const { data: serviceTypes = [] } = useQuery({
    queryKey: ["service-types"],
    queryFn: ({ signal }) => fetchServiceTypes(signal),
    retry: false,
    meta: { source: "fetchServiceTypes" },
  })

  const nameTaken = useNameTaken(TEMPLATE_NAMES, name)

  // The template as the server would receive it, by the schema that owns its
  // rules: trimmed, within its lengths. What is sent is what it accepts.
  const parsed = CreateSessionTemplateSchema.safeParse({
    name,
    description,
    programIds,
    serviceTypeIds,
  })
  const complete = parsed.success && !nameTaken

  const create = useMutation({
    mutationFn: (template: CreateSessionTemplate) =>
      createSessionTemplate(template),
    onSuccess: (created) => navigate(pathHelpers.toSessionTemplate(created.id)),
    onError: (err) => {
      // A 4xx carries a reason the administrator can act on and is shown as
      // sent below; only an unexpected failure is a fault worth recording.
      if (!isExpectedApiError(err)) {
        trackError(err, { source: "createSessionTemplate" })
      }
    },
  })
  const saving = create.isPending
  const saveError = create.error
    ? isExpectedApiError(create.error)
      ? create.error.message
      : "Could not create the session template. Please try again."
    : null

  return (
    <>
      <SessionTemplateBuilder
        // The headings follow the name as it is typed, so the builder is
        // already titled before it is saved.
        name={name.trim()}
        notice={saveError && <ErrorCallout>{saveError}</ErrorCallout>}
        details={
          <>
            <SessionTemplateNameFields
              name={name}
              description={description}
              onNameChange={setName}
              onDescriptionChange={setDescription}
              nameTaken={nameTaken}
            />

            <MultiSelectField
              label="Programmes"
              options={programs}
              selectedIds={programIds}
              onChange={setProgramIds}
              placeholder="Search programmes"
            />

            <MultiSelectField
              label="Service types"
              options={serviceTypes}
              selectedIds={serviceTypeIds}
              onChange={setServiceTypeIds}
              placeholder="Search service types"
            />
          </>
        }
        actions={
          <>
            <AdminButton
              variant="outline"
              ref={cancelRef}
              onClick={() => setLeaving(true)}
            >
              Cancel
            </AdminButton>
            <AdminButton
              // aria-disabled leaves the button clickable, so the guard is here.
              onClick={() =>
                parsed.success &&
                complete &&
                !saving &&
                create.mutate(parsed.data)
              }
              ariaDisabled={!complete || saving}
            >
              Save template
            </AdminButton>
          </>
        }
      >
        <EmptyCanvas>
          Adding modules to a template is the next step.
        </EmptyCanvas>
      </SessionTemplateBuilder>

      <DiscardChangesDialog
        open={leaving}
        onKeepEditing={() => {
          setLeaving(false)
          // Back where the question came from, so the keyboard does not start
          // over at the top of the page.
          cancelRef.current?.focus()
        }}
        onDiscard={() => navigate(paths.sessionTemplates)}
      />
    </>
  )
}
